import React, { useEffect, useState } from 'react'
import { List } from 'immutable'
import _size from 'lodash/size'
import Button, { TYPES } from '../../Button/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../Loading/index'
import { getPrimary } from '../../../services/languages/index'
import { useGetPlanGridData } from '../../../services/plans/hooks'
import { BILLING_PLAN_GRID_LOCATION_CHECKOUT } from '../../../services/plans/utils'
import { ADYEN_PAYMENT_TYPE_GOOGLEPAY } from '../../../services/adyen/index'
import { getPaymentMethods } from '../utils'
import CartSinglePagePaymentSecure from './CartSinglePagePaymentSecure'

/**
 * Component to handle the payment placeholder
 * @param {Map} props.staticText - Static text translations
 * @param {Map} props.auth - Auth state
 * @param {Map} props.user - User state
 * @param {Map} props.plans - Plans state
 * @param {Function} props.onCreateAccount - Function to create account
 * @returns {React.ReactNode}
 */
function CartSinglePagePaymentPlaceholder ({
  auth,
  user,
  plans,
  staticText,
  userAccount,
  disclaimer,
  onCreateAccount,
}) {
  const authJwt = auth.get('jwt')
  const isLoggedIn = !!authJwt
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const [paymentMethods, setPaymentMethods] = useState([])
  const [processingPaymentMethods, setProcessingPaymentMethods] = useState(false)

  const baseClass = 'cart-single-page__payment-placeholder'
  let plansList = List()

  // Get plan data using GraphQL
  plansList = useGetPlanGridData({
    userLanguage,
    authJwt,
    page: BILLING_PLAN_GRID_LOCATION_CHECKOUT,
  })

  const hasPaymentMethods = !!_size(paymentMethods)
  const plansCurrencyIso = plansList.getIn([0, 'currencyIso']) || ''

  // Load available payment methods when component mounts
  useEffect(() => {
    async function loadPaymentMethods () {
      const response = await getPaymentMethods({
        auth,
        user,
        plans,
        userAccount,
      })

      setPaymentMethods(response)
      setProcessingPaymentMethods(false)
    }

    if (plansCurrencyIso && isLoggedIn && !hasPaymentMethods && !processingPaymentMethods) {
      setProcessingPaymentMethods(true)
      loadPaymentMethods()
    }
  }, [auth, user, plansCurrencyIso])

  // Render authenticated user with payment methods
  if (hasPaymentMethods && isLoggedIn) {
    return (
      <div className={`${baseClass} ${baseClass}--auth`}>
        <CartSinglePagePaymentSecure staticText={staticText} />
        {paymentMethods.map((payment, index) => {
          if (payment.type === ADYEN_PAYMENT_TYPE_GOOGLEPAY) {
            return (
              <div key={index} className={`${baseClass}-method ${baseClass}-method--dark`}>
                <span>{staticText.getIn(['data', payment.type])}</span>
                <span className={`${baseClass}-method-logo ${baseClass}-logo--${payment.type}-dark`}></span>
              </div>
            )
          }
          return (
            <div key={index} className={`${baseClass}-method`}>
              <span className={`${baseClass}-method-logo ${baseClass}-logo--${payment.type}`}></span>
              <span>{staticText.getIn(['data', payment.type])}</span>
            </div>
          )
        })}
        {disclaimer}
      </div>
    )
  }

  // Render anonymous user
  if (!isLoggedIn) {
    return (
      <div className={`${baseClass} ${baseClass}--anon`}>
        <div className="cart-single-page__payment-placeholder-logos-container">
          <div className="cart-single-page__payment-placeholder-logo cart-single-page__payment-placeholder-logo--scheme" />
          <div className="cart-single-page__payment-placeholder-logo cart-single-page__payment-placeholder-logo--paypal" />
          <div className="cart-single-page__payment-placeholder-logo cart-single-page__payment-placeholder-logo--googlepay" />
          <div className="cart-single-page__payment-placeholder-logo cart-single-page__payment-placeholder-logo--applepay" />
          <div className="cart-single-page__payment-placeholder-logo cart-single-page__payment-placeholder-logo--mc" />
          <div className="cart-single-page__payment-placeholder-logo cart-single-page__payment-placeholder-logo--visa" />
        </div>
        <div className="cart-single-page__payment-placeholder-copy">
          <Button
            type={TYPES.LINK}
            onClick={onCreateAccount}
            className="cart-single-page__payment-placeholder-copy-button"
          >
            {staticText.getIn(['data', 'createAccount'])}
          </Button>
          {staticText.getIn(['data', 'toSee'])}
        </div>
      </div>
    )
  }

  // Render loading state
  return (
    <div className={`${baseClass} ${baseClass}--loading`}>
      <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
    </div>
  )
}

export default CartSinglePagePaymentPlaceholder
